import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../services/supabase'
import { HQ_PRIORITIES, HQ_TASK_STATUSES, type HqTaskPriority, type HqTaskStatus } from './constants'
import { useSimpleHqAccess } from './useSimpleHqAccess'
import { formatShortDate, overlapsDates, todayIso } from './hqUtils'

type Project = {
  id: string
  name: string
  summary: string | null
  related_url: string | null
  archived: boolean
}

type Task = {
  id: string
  title: string
  description: string | null
  assignee_user_id: string | null
  due_at: string | null
  priority: HqTaskPriority
  status: HqTaskStatus
  related_url: string | null
  labels: string[]
  last_update_at: string | null
}

type Update = { id: string; status: string; note: string; created_at: string; author_id: string | null }
type RosterRow = { user_id: string; email: string }
type LeaveRow = { user_id: string; start_date: string; end_date: string; status: string }

export function ProjectDetailView() {
  const { projectId } = useParams()
  const { user } = useAuth()
  const { companyId, isApprover } = useSimpleHqAccess()
  const [project, setProject] = useState<Project | null>(null)
  const [tasks, setTasks] = useState<Task[]>([])
  const [roster, setRoster] = useState<RosterRow[]>([])
  const [leaves, setLeaves] = useState<LeaveRow[]>([])
  const [selectedTask, setSelectedTask] = useState<string | null>(null)
  const [updates, setUpdates] = useState<Update[]>([])
  const [statusFilter, setStatusFilter] = useState('all')
  const [ownerFilter, setOwnerFilter] = useState('all')
  const [labelFilter, setLabelFilter] = useState('')
  const [note, setNote] = useState('')
  const [newStatus, setNewStatus] = useState<HqTaskStatus>('active')
  const [form, setForm] = useState({
    title: '',
    description: '',
    assignee_user_id: '',
    due_at: '',
    priority: 'medium' as HqTaskPriority,
    related_url: '',
    labels: '',
  })
  const [capacityWarn, setCapacityWarn] = useState<string | null>(null)

  const load = async () => {
    if (!projectId) return
    const [{ data: p }, { data: t }, { data: r }, { data: lv }] = await Promise.all([
      supabase.from('hq_projects').select('id, name, summary, related_url, archived').eq('id', projectId).maybeSingle(),
      supabase
        .from('hq_tasks')
        .select('id, title, description, assignee_user_id, due_at, priority, status, related_url, labels, last_update_at')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false }),
      supabase.rpc('simple_hq_roster'),
      supabase
        .from('leave_requests')
        .select('user_id, start_date, end_date, status')
        .eq('company_id', companyId)
        .eq('status', 'approved'),
    ])
    setProject(p as Project | null)
    setTasks((t as Task[]) || [])
    setRoster((r as RosterRow[]) || [])
    setLeaves((lv as LeaveRow[]) || [])
  }

  useEffect(() => {
    void load()
  }, [projectId, companyId])

  const emailById = useMemo(() => {
    const m: Record<string, string> = {}
    for (const r of roster) m[r.user_id] = r.email
    return m
  }, [roster])

  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false
      if (ownerFilter !== 'all' && (t.assignee_user_id || '') !== ownerFilter) return false
      if (labelFilter.trim()) {
        const lab = labelFilter.trim().toLowerCase()
        if (!(t.labels || []).some((x) => x.toLowerCase().includes(lab))) return false
      }
      return true
    })
  }, [tasks, statusFilter, ownerFilter, labelFilter])

  useEffect(() => {
    if (!form.assignee_user_id || !form.due_at) {
      setCapacityWarn(null)
      return
    }
    const due = form.due_at
    const windowStart = due
    const hit = leaves.find(
      (l) => l.user_id === form.assignee_user_id && overlapsDates(windowStart, due, l.start_date, l.end_date),
    )
    setCapacityWarn(
      hit
        ? `Capacity warn: assignee has approved leave ${hit.start_date}–${hit.end_date} overlapping the due date.`
        : null,
    )
  }, [form.assignee_user_id, form.due_at, leaves])

  const openTask = async (id: string) => {
    setSelectedTask(id)
    const task = tasks.find((t) => t.id === id)
    if (task) setNewStatus(task.status)
    const { data } = await supabase
      .from('hq_task_updates')
      .select('id, status, note, created_at, author_id')
      .eq('task_id', id)
      .order('created_at', { ascending: false })
    setUpdates((data as Update[]) || [])
  }

  const createTask = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isApprover || !projectId) return
    const labels = form.labels
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean)
    const { data, error } = await supabase
      .from('hq_tasks')
      .insert({
        company_id: companyId,
        project_id: projectId,
        title: form.title.trim(),
        description: form.description.trim() || null,
        assignee_user_id: form.assignee_user_id || null,
        due_at: form.due_at || null,
        priority: form.priority,
        related_url: form.related_url.trim() || null,
        labels,
        status: 'backlog',
        created_by: user?.id,
      })
      .select('id')
      .single()
    if (error) {
      alert(error.message)
      return
    }
    if (form.assignee_user_id && data?.id) {
      await supabase.functions.invoke('simple-hq-notify', {
        body: { kind: 'task_assigned', taskId: data.id },
      })
    }
    setForm({
      title: '',
      description: '',
      assignee_user_id: '',
      due_at: '',
      priority: 'medium',
      related_url: '',
      labels: '',
    })
    await load()
  }

  const saveProgress = async () => {
    if (!selectedTask || !note.trim()) {
      alert('Progress note required when changing status')
      return
    }
    const task = tasks.find((t) => t.id === selectedTask)
    if (!task) return
    const { error: uErr } = await supabase.from('hq_task_updates').insert({
      company_id: companyId,
      task_id: selectedTask,
      author_id: user?.id,
      status: newStatus,
      note: note.trim(),
    })
    if (uErr) {
      alert(uErr.message)
      return
    }
    const patch: Record<string, unknown> = {
      status: newStatus,
      last_update_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }
    const { error } = await supabase.from('hq_tasks').update(patch).eq('id', selectedTask)
    if (error) alert(error.message)
    else {
      setNote('')
      await load()
      await openTask(selectedTask)
    }
  }

  const reassign = async (taskId: string, assignee: string) => {
    if (!isApprover) return
    const task = tasks.find((t) => t.id === taskId)
    const due = task?.due_at
    if (assignee && due) {
      const hit = leaves.find((l) => l.user_id === assignee && overlapsDates(due, due, l.start_date, l.end_date))
      if (hit && !confirm(`Assignee has leave ${hit.start_date}–${hit.end_date}. Assign anyway?`)) return
    }
    const { error } = await supabase
      .from('hq_tasks')
      .update({ assignee_user_id: assignee || null, updated_at: new Date().toISOString() })
      .eq('id', taskId)
    if (error) alert(error.message)
    else {
      if (assignee) {
        await supabase.functions.invoke('simple-hq-notify', {
          body: { kind: 'task_assigned', taskId },
        })
      }
      await load()
    }
  }

  if (!project) return <p className="hq-empty">Loading project…</p>

  return (
    <div>
      <p className="hq-eyebrow">
        <Link to="/simple-hq/projects">Projects</Link>
      </p>
      <h1 className="hq-title">{project.name}</h1>
      {project.summary && <p className="hq-sub">{project.summary}</p>}
      {project.related_url && (
        <p className="mb-3 text-sm">
          Related:{' '}
          <a className="text-[var(--hq-accent)]" href={project.related_url} target="_blank" rel="noreferrer">
            {project.related_url}
          </a>
        </p>
      )}

      <div className="hq-filters">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Status">
          <option value="all">All statuses</option>
          {HQ_TASK_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} aria-label="Owner">
          <option value="all">All owners</option>
          {roster.map((r) => (
            <option key={r.user_id} value={r.user_id}>
              {r.email}
            </option>
          ))}
        </select>
        <input
          placeholder="Label filter"
          value={labelFilter}
          onChange={(e) => setLabelFilter(e.target.value)}
          className="border rounded px-2 py-1 text-sm"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="hq-panel">
          <p className="hq-eyebrow">Tasks ({filteredTasks.length})</p>
          <ul className="hq-list">
            {filteredTasks.map((t) => {
              const overdue = t.due_at && t.due_at < todayIso() && t.status !== 'done' && t.status !== 'cancelled'
              return (
                <li key={t.id}>
                  <button type="button" className="font-semibold text-left text-[var(--hq-accent)]" onClick={() => void openTask(t.id)}>
                    {t.title}
                  </button>
                  <div className="text-xs text-[var(--hq-muted)]">
                    <span className="hq-badge mr-1">{t.status}</span>
                    <span className={`hq-badge hq-badge-${t.priority === 'urgent' || t.priority === 'high' ? t.priority : 'pending'} mr-1`}>
                      {t.priority}
                    </span>
                    {emailById[t.assignee_user_id || '']?.split('@')[0] || 'unassigned'}
                    {t.due_at ? ` · ${formatShortDate(t.due_at)}` : ''}
                    {overdue ? ' · overdue' : ''}
                    {(t.labels || []).length ? ` · ${t.labels.join(', ')}` : ''}
                  </div>
                  {isApprover && (
                    <select
                      className="mt-1 text-xs border rounded"
                      value={t.assignee_user_id || ''}
                      onChange={(e) => void reassign(t.id, e.target.value)}
                      aria-label="Reassign"
                    >
                      <option value="">Unassigned</option>
                      {roster.map((r) => (
                        <option key={r.user_id} value={r.user_id}>
                          {r.email}
                        </option>
                      ))}
                    </select>
                  )}
                </li>
              )
            })}
          </ul>
        </div>

        <div>
          {selectedTask && (
            <div className="hq-panel mb-4">
              <p className="hq-eyebrow">Task detail</p>
              <p className="font-semibold mb-2">{tasks.find((t) => t.id === selectedTask)?.title}</p>
              <div className="hq-field">
                <label>Status</label>
                <select value={newStatus} onChange={(e) => setNewStatus(e.target.value as HqTaskStatus)}>
                  {HQ_TASK_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="hq-field">
                <label>Progress note (required)</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
              <button type="button" className="hq-btn hq-btn-primary" onClick={() => void saveProgress()}>
                Save progress
              </button>
              <ul className="hq-list mt-3">
                {updates.map((u) => (
                  <li key={u.id}>
                    <span className="hq-badge mr-1">{u.status}</span>
                    {u.note}
                    <div className="text-xs text-[var(--hq-muted)]">{new Date(u.created_at).toLocaleString()}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {isApprover && (
            <form className="hq-panel" onSubmit={createTask}>
              <p className="hq-eyebrow">Assign task</p>
              <div className="hq-field">
                <label>Title</label>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
              </div>
              <div className="hq-field">
                <label>Description</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="hq-row">
                <div className="hq-field">
                  <label>Assignee</label>
                  <select
                    value={form.assignee_user_id}
                    onChange={(e) => setForm({ ...form, assignee_user_id: e.target.value })}
                  >
                    <option value="">Unassigned</option>
                    {roster.map((r) => (
                      <option key={r.user_id} value={r.user_id}>
                        {r.email}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="hq-field">
                  <label>Due</label>
                  <input type="date" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} />
                </div>
                <div className="hq-field">
                  <label>Priority</label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value as HqTaskPriority })}
                  >
                    {HQ_PRIORITIES.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="hq-field">
                <label>Labels (comma-separated)</label>
                <input value={form.labels} onChange={(e) => setForm({ ...form, labels: e.target.value })} />
              </div>
              <div className="hq-field">
                <label>Related URL</label>
                <input value={form.related_url} onChange={(e) => setForm({ ...form, related_url: e.target.value })} />
              </div>
              {capacityWarn && <div className="hq-warn-box">{capacityWarn}</div>}
              <button type="submit" className="hq-btn hq-btn-primary">
                Create & assign
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
